import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { tenantService } from '../../../services/tenantService';
import OverviewTab from './components/Overview';
import SettingsTab from './components/Settings';
import BrandingTab from './components/Branding';
import DomainsTab from './components/Domains';
import VoiceCredentials from './components/VoiceCredentials';
import { Tabs } from '../../../components/ui/Tabs';

const TenantConfig: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();

  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchTenantDetails = async (id: string) => {
    try {
      setLoading(true);
      const res: any = await tenantService.get(id);
      setTenant(res?.stats);
    } catch (err) {
      console.error('Failed to load tenant details', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetails(tenantId);
    }
  }, [tenantId]);

  // Branding and Domains are whitelabel features, so they are only offered to a
  // whitelabel tenant. The endpoints enforce the same rule — hiding a tab
  // prevents a wasted click, it is not the control.
  const isWhitelabeled = Boolean(tenant?.tenant?.isWhitelabeled);

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'settings', name: 'Settings' },
    ...(isWhitelabeled
      ? [
          { id: 'branding', name: 'Branding' },
          { id: 'domains', name: 'Domains' },
          { id: 'voice', name: 'Voice' },
        ]
      : []),
  ];

  return (
    <Page contentClassName="pt-0 pb-6">
      <div className="border-b border-agentic-gray-200 bg-white">
        <div className="py-6">
          <PageHeader
            className="py-0"
            title={tenant?.tenant?.name || 'Tenant'}
            subtitle="Tenant Configuration"
            aside={
              <Button variant="ghost" onClick={() => navigate('/tenant-listing')}>
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </Button>
            }
          />
        </div>
      </div>

      <Section className="py-4">
        <Card className="mb-4 p-0">
          <Tabs variant="pill" items={tabs.map((tab) => ({ id: tab.id, label: tab.name }))} value={activeTab} onChange={setActiveTab} />

          <div className="min-h-[300px] p-6">
            {loading ? (
              <div className="text-agentic-text-secondary">Loading...</div>
            ) : activeTab === 'overview' ? (
              <OverviewTab tenant={tenant} />
            ) : activeTab === 'settings' ? (
              <SettingsTab
                tenant={tenant?.tenant}
                refetch={() => {
                  if (tenantId) {
                    fetchTenantDetails(tenantId);
                  }
                }}
              />
            ) : activeTab === 'branding' && tenantId && isWhitelabeled ? (
              <BrandingTab tenantId={tenantId} />
            ) : activeTab === 'domains' && tenantId && isWhitelabeled ? (
              <DomainsTab tenantId={tenantId} />
            ) : activeTab === 'voice' && tenantId && isWhitelabeled ? (
              <VoiceCredentials tenantId={tenantId} />
            ) : (
              <OverviewTab tenant={tenant} />
            )}
          </div>
        </Card>
      </Section>
    </Page>
  );
};

export default TenantConfig;
